import { useState } from 'react'
import { Link } from 'react-router-dom'
import axios from 'axios'
import { useAuth } from '../context/AuthContext'
import { useCoinBalance } from '../hooks/useCoinBalance'

const AMOUNTS = [50, 100, 250, 500, 1000]

export default function TipModal({ storyId, storyTitle, authorName, onClose, onSuccess }) {
  const { getToken } = useAuth()
  const { balance, loading: balanceLoading, refresh } = useCoinBalance()
  const [selected, setSelected] = useState(null)
  const [customMode, setCustomMode] = useState(false)
  const [customValue, setCustomValue] = useState('')
  const [sending, setSending] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState(null)

  const tipAmount = customMode ? (parseInt(customValue, 10) || 0) : selected

  async function handleConfirm() {
    if (!tipAmount || sending) return
    if (customMode) {
      if (!Number.isInteger(tipAmount) || tipAmount < 51) {
        setError('Custom tip must be a whole number of 51 coins or more.')
        return
      }
      if (tipAmount > balance) {
        setError('Insufficient balance.')
        return
      }
    }
    setSending(true)
    setError(null)
    try {
      const token = await getToken()
      const idempotencyKey = (crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`)
      await axios.post('/api/coins/tip', {
        storyId,
        amount: tipAmount,
      }, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Idempotency-Key': idempotencyKey,
        },
      })
      setSuccess(true)
      refresh()
      if (onSuccess) onSuccess(tipAmount)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to send tip. Please try again.')
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose} onKeyDown={(e) => { if (e.key === 'Escape') onClose() }}>
      <div className="tip-modal" role="dialog" aria-modal="true" aria-label="Tip author" onClick={e => e.stopPropagation()}>
        {success ? (
          <div className="tip-modal__success">
            <div className="tip-modal__checkmark">✓</div>
            <h3 className="tip-modal__success-title">Tip sent!</h3>
            <p className="tip-modal__success-desc">
              You tipped <strong>{tipAmount} coins</strong> on <em>{storyTitle}</em>
            </p>
            <button className="btn btn-primary tip-modal__done-btn" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <div className="tip-modal__header">
              <h3 className="modal-title">Tip {authorName}</h3>
              <p className="modal-desc">
                Show your appreciation for <em>{storyTitle}</em>
              </p>
              <button className="tip-modal__close" onClick={onClose} aria-label="Close">&times;</button>
            </div>

            <div className="tip-modal__balance">
              🪙 Your balance: <strong>{balanceLoading ? '…' : balance.toLocaleString()}</strong> coins
            </div>

            <div className="tip-modal__amounts">
              {AMOUNTS.map(amount => (
                <button
                  key={amount}
                  className={`coin-amount-btn${!customMode && selected === amount ? ' coin-amount-btn--selected' : ''}`}
                  disabled={balance < amount}
                  onClick={() => { setSelected(amount); setCustomMode(false); setCustomValue('') }}
                >
                  <span className="coin-amount-btn__icon">🪙</span>
                  <span className="coin-amount-btn__value">{amount}</span>
                </button>
              ))}
              <button
                className={`coin-amount-btn${customMode ? ' coin-amount-btn--selected' : ''}`}
                onClick={() => { setCustomMode(true); setSelected(null) }}
              >
                <span className="coin-amount-btn__icon">✏️</span>
                <span className="coin-amount-btn__value">Custom</span>
              </button>
            </div>

            {customMode && (
              <div style={{ textAlign: 'center', margin: '8px 0' }}>
                <input
                  type="number"
                  min={51}
                  max={balance}
                  value={customValue}
                  onChange={e => { setCustomValue(e.target.value); setError(null) }}
                  aria-label="Custom tip amount"
                  placeholder="Enter amount (51+)"
                  style={{ width: 160, padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: '1rem', textAlign: 'center' }}
                  autoFocus
                />
              </div>
            )}

            {balance < Math.min(...AMOUNTS) && !balanceLoading && (
              <p style={{ fontSize: 13, textAlign: 'center', margin: '4px 0 0' }}>
                <Link to="/settings?tab=Payments" onClick={onClose} style={{ color: 'var(--purple)', fontWeight: 500 }}>
                  Get coins
                </Link>
              </p>
            )}

            {error && <p className="tip-modal__error">{error}</p>}

            <button
              className="btn btn-primary tip-modal__confirm-btn"
              disabled={!tipAmount || sending}
              onClick={handleConfirm}
            >
              {sending ? 'Sending…' : tipAmount ? `Tip ${tipAmount} coins` : 'Select an amount'}
            </button>
          </>
        )}
      </div>
    </div>
  )
}
